import type { ReactNode } from "react";
import { QuietField } from "../../components/QuietField";

// 報價單抬頭的甲方／乙方兩塊。
//
// 業主 2026-09-11 指定的版面:
//
//   ┌── 甲方 ───────────────────┐  ┌── 乙方 ───────────────────┐
//   │ 本公司名稱  星采設計有限公司 │  │ 客戶      [台灣胸腔… ▾]    │
//   │ 專案承辦人  [林品豪 ▾]      │  │ 統一編號  12345678         │
//   │ 承辦人電話  02-2314-4089#21 │  │ 聯絡人    ______________   │
//   │ 聯絡地址    台北市…         │  │ 聯絡電話  ______________   │
//   │ EMAIL      lin@…           │  │ EMAIL     ______________   │
//   └───────────────────────────┘  │ 公司地址  台北市…          │
//                                   └───────────────────────────┘
//
// 用 <fieldset> + <legend> 而不是 <div> + <h3>:兩塊裡有同名的「EMAIL」欄位,
// 螢幕閱讀器要先報出「甲方,群組」才分得出這是哪一邊的 EMAIL。
// 換成標題文字的話,那個分組關係只存在於視覺上。

export interface PartyBlockProps {
  /**
   * 「甲方」或「乙方」。**同時就是這一塊的可及名稱**,所以 <legend> 裡
   * 只放它:多塞一句說明進去,名稱就變成「甲方 我方,取自…」,
   * 而使用者(與測試)找的是「甲方」。
   */
  title: string;
  /** 說明這一塊的資料從哪裡來。放在 legend 之外,不影響可及名稱。 */
  hint?: string;
  children: ReactNode;
}

export function PartyBlock({ title, hint, children }: PartyBlockProps) {
  return (
    <fieldset className="m-0 min-w-0 rounded-[var(--radius)] border border-[var(--border)] px-3 pb-2 pt-1">
      <legend className="px-1 text-[13px] font-semibold text-[var(--text-2)]">{title}</legend>
      {hint ? <p className="m-0 mb-1 text-xs text-[var(--text-3)]">{hint}</p> : null}
      <div className="flex min-w-0 flex-col">{children}</div>
    </fieldset>
  );
}

export interface PartyReadOnlyFieldProps {
  label: string;
  value: string;
  /** 沒有值時說明為什麼空著,例如「選了承辦人就會帶出來」。 */
  placeholder?: string;
}

/**
 * 甲乙方那些推導出來的欄位。**不可只靠顏色表達停用。**
 *
 * 三件事一起給:
 * 1. `readOnly` —— 打不進去,滑鼠與鍵盤都改不動。
 * 2. `aria-disabled` —— 螢幕閱讀器讀得到「這一格不能改」,顏色以外的第二個管道。
 * 3. 唯讀底色 + 次要文字色(`quietControlClass` 的 CONTROL_READONLY)——
 *    反灰之後裡面那個 0 仍然要讀得清楚,這一組對比釘在 tokens-contrast 那支測試裡。
 *
 * 要改這些值就去它們真正的來源改(公司設定、使用者管理、客戶檔),
 * 在報價單上開一個手填欄位,同一份資料馬上會有兩種說法。
 */
export function PartyReadOnlyField({ label, value, placeholder }: PartyReadOnlyFieldProps) {
  return (
    <QuietField
      label={label}
      value={value}
      placeholder={placeholder}
      readOnly
      aria-disabled={true}
    />
  );
}
